import React, { useState } from 'react';
import { Text, Box } from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';

interface WeeklyChartProps {
  data?: { [dia: string]: number }; // Tiempo en segundos
}

const WeeklyChart: React.FC<WeeklyChartProps> = ({ data }) => {
  const { colorScheme } = useMantineColorScheme();
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const [hoveredValue, setHoveredValue] = useState<number>(0);

  // Orden de días (L-D)
  const diasOrden = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
  const diasAbreviados = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  // Procesar datos
  const tiemposPorDia = diasOrden.map(dia => data?.[dia] || 0);
  const maxTiempo = Math.max(...tiemposPorDia, 1); // Evitar división por 0
  // Referencia de escala: al menos 1h. Así un tiempo chico (p. ej. 15s) se ve como
  // un pelito y no llena la barra; si algún día superas 1h, escala a tu máximo real.
  const maxRef = Math.max(maxTiempo, 3600);

  // Geometría de las barras
  const baseline = 60;       // línea base
  const maxBarH = 48;        // alto máximo de barra
  const barW = 16;           // ancho de barra
  const posicionesX = [10, 40, 70, 100, 130, 160, 190];

  // Formatear tiempo
  const formatearTiempo = (segundos: number): string => {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;

    if (horas > 0) {
      return minutos > 0 ? `${horas}h ${minutos}m` : `${horas}h`;
    }
    if (minutos > 0) return `${minutos}m`;
    return `${segs}s`;
  };

  return (
    <Box style={{ position: 'relative', height: '60px', width: '100%' }}>
      <svg width="100%" height="100%" viewBox="0 0 200 80">
        <defs>
          <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#0ea5e9" />
          </linearGradient>
        </defs>

        {/* Barras redondeadas */}
        {posicionesX.map((x, i) => {
          const tiene = tiemposPorDia[i] > 0;
          const barH = tiene ? Math.max((tiemposPorDia[i] / maxRef) * maxBarH, 3) : 2;
          const y = baseline - barH;
          const activo = hoveredDay === diasOrden[i];
          return (
            <rect
              key={i}
              x={x - barW / 2}
              y={y}
              width={barW}
              height={barH}
              rx={4}
              fill={tiene ? 'url(#barGradient)' : (colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)')}
              opacity={hoveredDay && !activo ? 0.5 : 1}
              onMouseEnter={() => {
                setHoveredDay(diasOrden[i]);
                setHoveredValue(tiemposPorDia[i]);
              }}
              onMouseLeave={() => setHoveredDay(null)}
              style={{ cursor: 'pointer', transition: 'opacity 0.15s ease' }}
            />
          );
        })}

        {/* Etiquetas de días */}
        {diasAbreviados.map((day, i) => (
          <text
            key={i}
            x={posicionesX[i]}
            y="75"
            textAnchor="middle"
            fill={colorScheme === 'dark' ? '#94a3b8' : '#64748b'}
            fontSize="10"
            fontFamily="Inter, sans-serif"
            fontWeight={hoveredDay === diasOrden[i] ? "600" : "400"}
          >
            {day}
          </text>
        ))}
      </svg>

      {/* Tooltip */}
      {hoveredDay && (
        <Box
          style={{
            position: 'absolute',
            top: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
            borderRadius: '8px',
            padding: '8px 12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          <Text size="xs" fw={600} style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b' }}>
            {hoveredDay}
          </Text>
          <Text size="sm" fw={700} style={{ color: '#0ea5e9' }}>
            {formatearTiempo(hoveredValue)}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default WeeklyChart;
